import React, { createContext, useContext, useMemo } from 'react';
import { createStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { addMonths, isWithinInterval, startOfToday } from 'date-fns';
import {
  DateRange,
  DateRangeValid,
  extractTimeFrom,
  isDateRangeSelectable,
  isDateSelectable,
  calendarMonthsToDays,
  calendarMonthDates,
} from '../../../utils/date-utils';
import { resolveDayStates } from './calendarStateResolver';

export interface DayType {
  key: number;
  date: Date;
  displayDate: number;
  isActive: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  isFirst: boolean;
  isLast: boolean;
  isInRange: boolean;
  isVisible: boolean;
}

export type Days = DayType[];

export interface CalendarState {
  hideOverflowDates?: boolean;
  activeDates?: Date[];
  allowDeselect?: boolean;
  allowRangeSelection?: boolean;
  hoverDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  maxRange?: number;
  selectedDate?: Date;
  selectedDateRange: DateRange;
  selectedMonth: Date;
  disabledDates?: Date[];
  today: Date;
  days: Days;
  offsetDays: {
    [key: number]: number[];
  };
  numberOfMonths?: number;
  allowPastDates?: boolean; 
}

const today = startOfToday();

const initialState: CalendarState = {
  activeDates: [],
  allowDeselect: true,
  allowRangeSelection: false,
  hideOverflowDates: false,
  days: [],
  disabledDates: [],
  selectedDateRange: { start: undefined, end: undefined },
  selectedMonth: today,
  today,
  numberOfMonths: 1,
  offsetDays: {},
  allowPastDates: false
};

export type CalendarInitialProps = {
  initialDate?: Date;
  initialDateRange?: DateRangeValid;
} & Pick<
  CalendarState,
  | 'activeDates'
  | 'allowDeselect'
  | 'allowRangeSelection'
  | 'disabledDates'
  | 'hideOverflowDates'
  | 'maxDate'
  | 'maxRange'
  | 'minDate'
  | 'numberOfMonths'
  | 'allowPastDates'
>;



export const createCalendarStore = (initProps: CalendarInitialProps) => {
  return createStore<CalendarState>()(
    subscribeWithSelector(() => {
      const { initialDate, initialDateRange, numberOfMonths, ...rest } =
        initProps;

      //This needs to be reworked to take disabled dates and such into account
      const selectedDate =
        initialDate && isDateSelectable(initialDate)
          ? extractTimeFrom(initialDate)
          : undefined;

      //This needs to be reworked to take disabled dates and such into account
      const selectedDateRange =
        initialDateRange &&
          initialDateRange.start &&
          isDateRangeSelectable(initialDateRange)
          ? {
            start: extractTimeFrom(initialDateRange.start),
            end: extractTimeFrom(initialDateRange.end),
          }
          : { start: undefined, end: undefined };

      const m = selectedDate || selectedDateRange.start || today;

      const selectedMonth = new Date(m.getFullYear(), m.getMonth());
      const days = calendarMonthsToDays(selectedMonth, numberOfMonths);

      const stateToSet = {
        ...initialState,
        ...rest,
        days,
        numberOfMonths,
        selectedDate,
        selectedDateRange,
        selectedMonth,
      } as CalendarState;

      const resolvedState = {
        ...stateToSet,
        days: resolveDayStates(stateToSet),
      };

      const offsetDays = Array(numberOfMonths)
        .fill(0)
        .reduce((acc, curr, currentIndex) => {
          const daysForOffsetMonth = calendarMonthDates(
            addMonths(selectedMonth, currentIndex),
          );
          acc[currentIndex] = resolvedState.days
            .filter((day) => {
              const interval = {
                start: daysForOffsetMonth[0],
                end: daysForOffsetMonth[daysForOffsetMonth.length - 1],
              };
              return isWithinInterval(day.date, interval);
            })
            .map((day) => day.key);
          return acc;
        }, {});

      return {
        ...resolvedState,
        offsetDays,
      };
    })
  );
};


type CalendarStore = ReturnType<typeof createCalendarStore>;

const CalendarContext = createContext<CalendarStore | null>(null);

export const CalendarProvider = ({
  initialState,
  children,
}: {
  initialState: CalendarInitialProps;
  children: React.ReactNode;
}) => {
  const store = useMemo(() => createCalendarStore(initialState), [initialState]);

  return (
    <CalendarContext.Provider value={store}>
      {children}
    </CalendarContext.Provider>
  );
};



export const useCalendarStoreAPI = () => {
  const store = useContext(CalendarContext);
  if (!store) {
    throw new Error('useCalendarStoreAPI must be used within a CalendarProvider');
  }
  return store;
};


export const useCalendarStore = <T,>(
  selector: (state: CalendarState) => T,
  equalityFn?: (a: T, b: T) => boolean
) => {
  const store = useCalendarStoreAPI();
  return useStoreWithEqualityFn(store, selector, equalityFn);
};